"use client"

import { Box } from "@chakra-ui/react/box"
import {
  domAnimation,
  LazyMotion,
  m,
  MotionValue,
  useSpring,
} from "framer-motion"
/* eslint_disable max-len */
import React, { useEffect, useReducer } from "react"

import LiquidGlass from "./LiquidGlass/LiquidGlass"

interface ScrollUpState {
  hidden: boolean
  hasMounted: boolean
  isInitialLoad: boolean
}

type ScrollUpAction =
  | { type: "mount" }
  | { type: "set-hidden"; value: boolean }
  | { type: "finish-initial-load" }
  | { type: "reset-to-top" }

function scrollUpReducer(
  state: ScrollUpState,
  action: ScrollUpAction
): ScrollUpState {
  switch (action.type) {
    case "mount":
      return { ...state, hasMounted: true }
    case "set-hidden":
      return state.hidden === action.value
        ? state
        : { ...state, hidden: action.value }
    case "finish-initial-load":
      return { ...state, isInitialLoad: false }
    case "reset-to-top":
      return { ...state, hidden: true, isInitialLoad: false }
    default:
      return state
  }
}

interface ScrollUpProps {
  pageProgress: MotionValue<number>
}

const ScrollUp: React.FC<ScrollUpProps> = ({ pageProgress }) => {
  const [{ hasMounted, hidden, isInitialLoad }, dispatchScrollUp] = useReducer(
    scrollUpReducer,
    {
      hasMounted: false,
      hidden: true,
      isInitialLoad: true,
    }
  )
  const progressRef = React.useRef(0)
  // console.log(pageProgress.current);
  const scrollYProgress = useSpring(0, {
    damping: 30,
    restDelta: 0.001,
    stiffness: 100,
  })

  useEffect(() => {
    const update = () => {
      const currentProgress = pageProgress.get()
      // Only update if the progress has actually changed and is valid
      if (
        currentProgress !== progressRef.current &&
        currentProgress >= 0 &&
        currentProgress <= 1
      ) {
        progressRef.current = currentProgress
        scrollYProgress.set(currentProgress)
        // Only update hidden state if we're past initial load
        if (!isInitialLoad) {
          dispatchScrollUp({
            type: "set-hidden",
            value: currentProgress === 0,
          })
        }
      }
    }

    // Initial check
    update()
    dispatchScrollUp({ type: "mount" })

    // Subscribe to scroll progress changes
    const unsubscribe = pageProgress.on("change", update)

    // Force an update after a short delay to ensure we catch any missed updates
    const timeoutId = setTimeout(() => {
      // Reset to 0 if we're at the top of the page
      if (window.scrollY === 0) {
        progressRef.current = 0
        scrollYProgress.set(0)
        dispatchScrollUp({ type: "reset-to-top" })
      } else {
        update()
        dispatchScrollUp({ type: "finish-initial-load" })
      }
    }, 100)

    return () => {
      unsubscribe()
      clearTimeout(timeoutId)
    }
  }, [pageProgress, scrollYProgress, isInitialLoad])

  const variants = {
    hidden: { x: 125 },
    visible: { x: 0 },
  }

  // Don't render anything until we've confirmed the scroll position and initial load is complete
  if (!hasMounted || isInitialLoad) {
    return null
  }

  return (
    <LazyMotion features={domAnimation}>
      <Box
        asChild
        bottom={{ base: "0.7rem", lg: "1.6rem" }}
        css={{
          opacity: hasMounted ? 1 : 0,
          transform: "translateX(125px) translateZ(0px)",
        }}
        cursor="pointer"
        data-print-scroll-up=""
        height="46px"
        position="fixed"
        right="0.7rem"
        transition="all 0.6s ease"
        width="46px"
        zIndex="1000"
      >
        <m.button
          animate={!hasMounted || hidden ? "hidden" : "visible"}
          aria-label="Scroll to top"
          data-cursor-label="Scroll to top"
          onClick={() => {
            window.scrollTo({ behavior: "smooth", top: 0 })
          }}
          style={{
            appearance: "none",
            background: "transparent",
            border: 0,
            color: "inherit",
            padding: 0,
          }}
          title="Scroll to top"
          type="button"
          variants={variants}
        >
          <LiquidGlass
            _hover={{
              bgColor: "primary.500",
              color: "white",
            }}
            height={"full"}
            width={"full"}
          >
            <svg
              height="100%"
              id="progress"
              style={{
                height: "48px",
                left: 0,
                position: "absolute",
                top: -2,
                transform: "rotate(-90deg)",
                width: "48px",
              }}
              viewBox="0 0 100 100"
              width="100%"
            >
              <m.circle
                className="indicator"
                cx="50"
                cy="50"
                fill="none"
                pathLength="1"
                r="46"
                stroke="var(--gil-colors-primary-fg)"
                strokeDashoffset="0"
                strokeLinecap="round"
                strokeWidth="8px"
                style={{ pathLength: scrollYProgress }}
              />
            </svg>
            <Box
              css={{
                "& > svg": {
                  transition: "color 200ms linear",
                },
              }}
              display="flex"
              left="50%"
              position="absolute"
              top="50%"
              transform="translate(-50%, -50%)"
              zIndex="2222"
            >
              <svg height="18" viewBox="0 0 330 330">
                <path
                  d="M100.606,100.606L150,51.212V315c0,8.284,6.716,15,15,15c8.284,0,15-6.716,15-15V51.212l49.394,49.394  C232.322,103.535,236.161,105,240,105c3.839,0,7.678-1.465,10.606-4.394c5.858-5.857,5.858-15.355,0-21.213l-75-75  c-5.857-5.858-15.355-5.858-21.213,0l-75,75c-5.858,5.857-5.858,15.355,0,21.213C85.251,106.463,94.749,106.463,100.606,100.606z"
                  fill="currentColor"
                />
              </svg>
            </Box>
          </LiquidGlass>
        </m.button>
      </Box>
    </LazyMotion>
  )
}

export default ScrollUp
